/**
 *
 * DynamicSideList
 *
 */

import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';

// styles
import './style.scss';

// antd component
import { Layout, Skeleton, PageHeader, Empty } from 'antd';
const { Sider } = Layout;
// components
import Preloader from 'components/Preloader';
import SideListItem from 'components/SideListItem';
import {
  SortButton,
  AddButton,
  SearchButton,
  FilterOptions,
} from 'components/DynamicSideList/components';

// utils
import history, { parseQuery, encodeQueryData } from 'utils/history';
import { getObjId } from 'utils/helpers/helper.general';
import { prepearFilteredOptions } from 'utils/helpers/helper.filter-sort';

import api, { setAuthorizationHeader } from 'utils/apis/axiosAPI';
// hooks
import { useAsync, useRefState } from 'utils/hooks';

// contexts
import { withTheme } from 'engine/Contexts/Theme.context';


function DynamicSideList({
  type,
  title,
  subTitle,
  sortProps,
  searchProps,
  filterProps,
  ascend,
  outputListItem,
  showPreventClass,
  firstRender,
  // main
  list,
  noMore,
  loadingList,
  totalAmount,
  initialID,
  activeElement,
  currentItemAction,
  loadParams,
  // actions
  changeParams,
  addAction,
  onClick,
  // contexts
  theme,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('side-list-wrapper', className);

  const currentUser = JSON.parse(localStorage.getItem('schoolinked_user'));
  const token = currentUser?.token;
  setAuthorizationHeader(token);

  const initSort = {
    ascend: ascend || false, // descend
    props: sortProps ? [...new Set([...sortProps])] : [], // 'description.firstname'
  };

  const scrollParentRef = useRef(null);

  const [showSearch, setShowSearch] = useState(
    loadParams?.searchStr.length > 0,
  );
  const [showFilter, setShowFilter] = useState(
    false /* || loadParams?.filterValue.length > 0, */,
  );

  const [currentParams, setCurrentParams] = useState(loadParams);

  const [searchVal, setSearchVal] = useState(loadParams?.searchStr || '');
  const [sort, setSort] = useState(initSort);
  const [filter, setFilter] = useState(
    loadParams?.filterValue?.length > 0
      ? typeof loadParams?.filterValue ==='string' ? loadParams?.filterValue?.split(',') : loadParams?.filterValue
      : [],
  );
  const [optionsForFilter, setOptionsForFilter] = useState([]);
  const [activeListItem, setActiveListItem] = useState(activeElement);

  let timeOutListScroll;

  // Get Types for filtering
  useEffect(() => {
    setCurrentParams(loadParams);

    // load items for filtering
    optionsForFilter?.length <= 0 && api.communications.fetchAllTypes().then(result => {
      let emptyResult = [
        {
          key: 'types',
          name: 'types',
          group: [
            {
              key: 'types',
              label: 'Types',
              name: 'types-all',
              value: 'types-all',
            },
          ],
        },
      ];
      if (result.length > 0) {
        setOptionsForFilter(result);
      } else {
        setOptionsForFilter(emptyResult);
      }
    });

    return () => {
      // this will clear setTimeout
      timeOutListScroll && clearTimeout(timeOutListScroll);
    };
  }, []);

  // Scroll to Active list element
  /*  useEffect(() => {
    if (activeListItem || currentItem) {
      const elemID = activeListItem
        ? getObjId(activeListItem)
        : getObjId(currentItem);
      const domListElement = document.getElementById(
        `${type}-infinite-scroll-web`,
      );
      if (elemID && domListElement) {
          timeOutListScroll = setTimeout(() => {
          const domElement = document.getElementById(elemID);
          //  const offsetElem = domElement.offsetTop;
          domElement && domListElement.scrollTo(0, domElement.offsetTop);
        }, 600); 
      }
    }
  }, [activeListItem, currentItem]); */

  // set active list Item
  useEffect(() => {
    if (getObjId(initialID) != getObjId(activeListItem)) {
      if (getObjId(initialID) == getObjId(activeElement)) {
        setFirstElemInArray(activeElement);
      } else {
        const listEl = list.find(el => getObjId(initialID) == getObjId(el));
        !!listEl ? setFirstElemInArray(listEl) : setFirstElemInArray(initialID);
      }
    } else if (!activeElement && list?.length > 0) {
      const firstElem = list[0];
      setFirstElemInArray(firstElem);
    }
  }, [initialID]);

  const setFirstElemInArray = firstElem => {
    if (firstElem) {
      const firstElemID = getObjId(firstElem);
      setActiveListItem(firstElem);
      currentItemAction(firstElemID, firstElem);
    }
  };

  // change active list item
  const changeCurrentItem = (elementId, element) => {
    if (getObjId(activeListItem) != getObjId(element)) {
      setActiveListItem(element);
      currentItemAction(elementId, element);
    }
  };

  /**
   * Search Actions
   */
  const onSearch = useCallback(
    searchValue => {
      if (!loadingList) {
        setCurrentParams(prev => {
          const newVal = {
            ...prev,
            page: 1,
            searchStr: searchValue,
          };
          changeParams(newVal);
          return newVal;
        });
      }
    },
    [loadingList],
  );
  const onCloseSearch = useCallback(() => {
    setSearchVal('');
    setCurrentParams(prev => {
      const newVal = { ...prev, searchStr: '' };
      changeParams(newVal);
      return newVal;
    });
  }, []);

  /**
   * Sort Actions
   */
  const onSort = useCallback(() => {
    const newSort = { ...sort, ascend: !sort.ascend };
    setSort(newSort);
    // const sortedList = getInitSortedList(list, newSort);
    const firstElem = list?.length > 0 && list[0];
    setFirstElemInArray(firstElem);
  }, [list]);

  /**
   * Add Actions
   */
  const onAddAction = useCallback(() => {
    setShowSearch(false);
    setShowFilter(false);
    setSearchVal('');
    setSort(initSort);
    setFilter([]);
    const firstElem = list?.length > 0 && list[0];
    setFirstElemInArray(firstElem);
    addAction();
  }, [list, initSort]);

  /**
   * Filter Actions
   */
  const onFilter = useCallback(checkedValues => {
    setFilter(checkedValues || []);
    setCurrentParams(prev => {
      const newVal = {
        ...prev,
        page: 1,
        filterValue: checkedValues,
      };
      changeParams(newVal);
      return newVal;
    });
  }, []);

  const resetFilter = useCallback(() => {
    setShowSearch(false);
    setSearchVal('');
    setFilter([]);
    setCurrentParams(prev => {
      const newVal = {
        ...prev,
        page: 1,
        searchStr: '',
        filterValue: [],
      };
      changeParams(newVal);
      return newVal;
    });
  }, [list]);

  // extra actions for side header
  const extraActions = [
    <SearchButton
      key="search-communication-bar"
      uniqueKey="search-communication"
      value={searchVal}
      loading={loadingList}
      show={showSearch}
      setShow={setShowSearch}
      onSearch={onSearch}
      onClose={onCloseSearch}
      onClick={() => setShowFilter(false)}
    />,
    <FilterOptions
      uniqueKey="filter-communication"
      value={filter}
      loading={loadingList}
      options={optionsForFilter}
      show={showFilter}
      setShow={setShowFilter}
      onFilter={onFilter}
      onReset={resetFilter}
    />,
    sortProps && (
      <SortButton
        key="sort-option-bar"
        uniqueKey="sort-communication"
        onSort={onSort}
        sortByAsc={sort.ascend}
      />
    ),
    <AddButton key="add-btn-bar" uniqueKey="addNew-communication" onClick={onAddAction} />,
  ];

  const getSubTitleLabel = type => {
    switch (type) {
      case 'users':
        return 'utilisateurs';

      case 'etudiants':
        return 'étudiants';

      default:
        return type;
    }
  };

  const loadMoreCallback = useCallback(
    (loadingList, noMore) => {
      if (!loadingList && !noMore) {
        setCurrentParams(prev => {
          const newVal = {
            ...prev,
            page: Number(loadParams?.page) + 1,
          };
          changeParams(newVal);
          return newVal;
        });
      }
    },
    [loadParams],
  );

  const showOpenOnSaveModalCallback = useCallback(() => {
    theme?.onOpenOnSaveModalCallback() 
    !!onClick && onClick();
  }, [theme?.isOpenModalOnSaveChanges, theme?.hasValueModalOnSaveChanges, onClick]);

  return (
    <Sider className={childClassNames} onClick={showOpenOnSaveModalCallback} ref={scrollParentRef}>
      <PageHeader
        className={`side-list-header ${showSearch ? 'searchIsShowing' : ''}`}
        title={title}
        subTitle={
          !!subTitle
            ? subTitle
            : `${totalAmount || list?.length || 0} ${getSubTitleLabel(type)}`
        }
        extra={extraActions}
      />
      <div
        key={`${type}-infinite-scroll-web`}
        id={`${type}-infinite-scroll-web`}
        className="infinite-scroll-web"
      >
        <InfiniteScroll
          key={`${type}-dyncamic-side-list`}
          id={`${type}-dyncamic-side-list`}
          className="dyncamic-side-list"
          initialLoad={false}
          pageStart={loadParams?.page || 1}
          loadMore={() => loadMoreCallback(loadingList, noMore)}
          hasMore={!noMore}
          useWindow={false}
          getScrollParent={function() {
            const parentRefContainer = document.querySelector(
              '.infinite-scroll-web',
            );
            return parentRefContainer;
          }}
          loader={<Preloader />}
        >
          {list?.length > 0 &&
            list?.map(
              item =>
                item?._id && (
                  <SideListItem
                    key={item?._id + '-dyncamic-side-item'}
                    type={type}
                    onClickAction={changeCurrentItem}
                    element={item}
                    activeElement={activeElement}
                    searchString={searchVal}
                  />
                ),
            )}
          {!loadingList && list?.length <= 0 && (
            <Empty
              key="empty-list-message"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                'Aucune donnée n’a été trouvée concernant votre demande'
              }
            />
          )}
        </InfiniteScroll>
      </div>
    </Sider>
  );
}

DynamicSideList.defaultProps = {
  type: 'utilisateurs',
  title: 'List of',
  subTitle: '',
  ascend: true,
};
DynamicSideList.propTypes = {
  type: PropTypes.oneOf([
    'utilisateurs',
    'classes',
    'сours',
    'etudiants',
    'absence',
    'communications',
  ]).isRequired,
  list: PropTypes.oneOfType([
    PropTypes.array.isRequired,
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
  ]),
  sortProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  searchProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  ascend: PropTypes.bool,
  currentItemAction: PropTypes.func.isRequired,
  addAction: PropTypes.func.isRequired,
};

export default withTheme(DynamicSideList);
